import { sendErrorEvent } from '@onyx/observability-v2/client';
import type { LoggerOptions } from 'pino';
import pino from 'pino';

import { messageKey } from '../constants';
import type { LogEvent, Logger } from '../types';
import { isLogEvent } from '../types';

const consoleOut =
  (consoleMethod: (message?: unknown, ...optionalParams: unknown[]) => void) =>
  (obj: object) => {
    if (isLogEvent(obj)) {
      consoleMethod(obj.message || obj.event, obj);
    } else {
      consoleMethod(obj);
    }
  };

export const clientLoggerOptions: LoggerOptions = {
  level: 'debug',
  messageKey,
  browser: {
    write: {
      fatal: consoleOut(console.error),
      error: consoleOut(console.error),
      warn: consoleOut(console.warn),
      info: consoleOut(console.log),
      debug: consoleOut(console.debug),
      trace: consoleOut(console.trace),
    },
    transmit: {
      level: 'error',
      send: (_, logEvent) => {
        const [firstArg, ...restArgs] = logEvent.messages;
        const customAttributes = {
          timestamp: logEvent.ts,
          restArgs,
          level: 'error',
        };
        if (firstArg instanceof Error) {
          sendErrorEvent(firstArg, 'logger_error', customAttributes);
          return;
        }

        if (isLogEvent(firstArg)) {
          const { event, message, err, ...rest } = firstArg;
          const error =
            err instanceof Error ? err : new Error(message || event);

          sendErrorEvent(error, 'logger_error', {
            event,
            ...customAttributes,
            ...rest,
          });
          return;
        }

        if (
          typeof firstArg?.err === 'object' &&
          firstArg.err instanceof Error
        ) {
          const { err, ...rest } = firstArg;
          sendErrorEvent(err, 'logger_error', { ...customAttributes, ...rest });
          return;
        }

        const error = new Error(JSON.stringify(firstArg));
        sendErrorEvent(error, 'logger_error', customAttributes);
      },
    },
  },
};

// Client logs are not redacted, so `raw` is the logger itself. pino builds each
// child on top of its parent, so the getter returns the child it is read from.
export const clientLogger: Logger<LogEvent> = Object.create(
  pino(clientLoggerOptions),
  {
    raw: {
      get() {
        return this;
      },
    },
  }
);
